import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArcadeMarquee } from './components/ArcadeMarquee';
import { AttractPressStart } from './components/AttractPressStart';
import { CharacterProfile } from './components/CharacterProfile';
import { CharacterSelectAttractScene } from './components/CharacterSelectAttractScene';
import { CharacterTransition } from './components/CharacterTransition';
import { ChampionUnlock } from './components/ChampionUnlock';
import { FinaleAttractScene } from './components/FinaleAttractScene';
import { FighterArmToast } from './components/FighterArmToast';
import { FinaleShortcutUnlock } from './components/FinaleShortcutUnlock';
import { FightingGame } from './components/FightingGame';
import { IntroSequence } from './components/IntroSequence';
import { SiteFooter } from './components/SiteFooter';
import { StoryAttractScene } from './components/StoryAttractScene';
import { TitleAttractScene } from './components/TitleAttractScene';
import { VersusAttractScene } from './components/VersusAttractScene';
import { characters } from './data/characters';
import { getStory } from './data/attract';
import { useAttractController } from './hooks/useAttractController';
import { useAttractAudio } from './hooks/useAttractAudio';
import { attractAudio, audioAsset } from './lib/attractAudio';
import { useAudioPreferences } from './lib/audioPreferences';
import { useEasterEggProgress } from './lib/easterEggProgress';
import type { AttractEntry, CharacterProfile as CharacterProfileType, TransitionStyle } from './types';

const introKey = 'smackers-intro-played';
const transitionStyles: TransitionStyle[] = ['ink', 'slash', 'panels', 'torn', 'impact', 'page'];

export default function SmackersExperience() {
  const reducedMotion = useMemo(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches, []);
  const finalePreview = useMemo(() => import.meta.env.DEV && new URLSearchParams(window.location.search).get('scene') === 'finale', []);
  const fightPreview = useMemo(() => import.meta.env.DEV && new URLSearchParams(window.location.search).get('scene') === 'fight', []);
  const fightPreviewMode = useMemo(() => import.meta.env.DEV && new URLSearchParams(window.location.search).get('fightMode') === '2p' ? '2p' : '1p', []);
  const [introVisible, setIntroVisible] = useState(() => !finalePreview && !fightPreview && sessionStorage.getItem(introKey) !== 'true');
  const [fastMode, setFastMode] = useState(false);
  const [armedToast, setArmedToast] = useState<{ character: CharacterProfileType; slot: number } | null>(null);
  const [turboNotice, setTurboNotice] = useState(false);
  const [championVisible, setChampionVisible] = useState(false);
  const [championShown, setChampionShown] = useState(false);
  const [finaleShortcutVisible, setFinaleShortcutVisible] = useState(false);
  const [finaleShortcutRoute, setFinaleShortcutRoute] = useState<{ finaleIndex: number } | null>(null);
  const [versusOverride, setVersusOverride] = useState<{ entryIndex: number; leftCharacterId: string; rightCharacterId: string } | null>(null);
  const [credits, setCredits] = useState(fightPreview ? 2 : 0);
  const [coinNotice, setCoinNotice] = useState<{ credits: number; sequence: number } | null>(null);
  const [fightMode, setFightMode] = useState<'1p' | '2p' | null>(fightPreview ? fightPreviewMode : null);
  const [shooterMode, setShooterMode] = useState(finalePreview);
  const [shooterRunKey, setShooterRunKey] = useState(0);
  const creditMilestonesRef = useRef(new Set<number>());
  const gamepadStartRef = useRef<boolean[]>([]);
  useAudioPreferences();
  const eggs = useEasterEggProgress();
  const playbackRate = fastMode ? 4 : 1;
  const controller = useAttractController(characters, !introVisible, playbackRate);
  const { entries, entry, entryIndex, imageIndex, frameProgress, sceneElapsedMs, begin, restart, jumpToEntry, pause, resume, isPaused } = controller;

  useEffect(() => {
    if (entry.type !== 'versus' || versusOverride?.entryIndex === entryIndex || eggs.armedFighterIds.length !== 2) return;
    const [leftCharacterId, rightCharacterId] = eggs.armedFighterIds;
    setVersusOverride({ entryIndex, leftCharacterId, rightCharacterId });
    eggs.consumeArmedFighters();
  }, [eggs, entry, entryIndex, versusOverride]);

  const effectiveEntry: AttractEntry = useMemo(() => entry.type === 'versus' && versusOverride?.entryIndex === entryIndex
    ? { ...entry, leftCharacterId: versusOverride.leftCharacterId, rightCharacterId: versusOverride.rightCharacterId, easterEgg: undefined }
    : entry, [entry, entryIndex, versusOverride]);

  useAttractAudio({
    enabled: !introVisible,
    entry: effectiveEntry,
    entryIndex,
    playbackMultiplier: playbackRate,
    sceneElapsedMs,
    nextEntry: entries[entryIndex + 1],
  });

  useEffect(() => {
    if (effectiveEntry.type !== 'story') return;
    const storyOrdinal = entries.slice(0, entryIndex + 1).filter((candidate) => candidate.type === 'story').length;
    if ((storyOrdinal !== 3 && storyOrdinal !== 5) || sceneElapsedMs < effectiveEntry.durationMs / 2 || creditMilestonesRef.current.has(storyOrdinal)) return;
    creditMilestonesRef.current.add(storyOrdinal);
    const nextCredits = storyOrdinal === 3 ? 1 : 2;
    setCredits(nextCredits);
    setCoinNotice({ credits: nextCredits, sequence: Date.now() });
    attractAudio.playIfUnlocked(audioAsset('/assets/audio/ui/coin-swallow.wav'), { kind: 'effect', gain: 1 });
    if (nextCredits === 2) attractAudio.playIfUnlocked(audioAsset('/assets/audio/ui/credit-ready.wav'), { kind: 'effect', gain: 0.95, delaySeconds: 0.52 });
    const timer = window.setTimeout(() => setCoinNotice((current) => current?.credits === nextCredits ? null : current), 2500);
    return () => window.clearTimeout(timer);
  }, [effectiveEntry, entries, entryIndex, sceneElapsedMs]);

  const startFightingGame = useCallback((mode: '1p' | '2p') => {
    if (credits < 2 || fightMode) return;
    pause();
    setShooterMode(false);
    setFightMode(mode);
    attractAudio.playIfUnlocked(audioAsset('/assets/audio/ui/credit-ready.wav'), { kind: 'effect', gain: 1, playbackRate: 1.08 });
  }, [credits, fightMode, pause]);

  useEffect(() => {
    if (credits < 2 || fightMode) return;
    let frame = 0;
    const poll = () => {
      const pads = navigator.getGamepads?.() ?? [];
      for (let index = 0; index < pads.length; index += 1) {
        const pressed = Boolean(pads[index]?.buttons[9]?.pressed);
        if (pressed && !gamepadStartRef.current[index]) startFightingGame(index >= 1 ? '2p' : '1p');
        gamepadStartRef.current[index] = pressed;
      }
      frame = requestAnimationFrame(poll);
    };
    frame = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(frame);
  }, [credits, fightMode, startFightingGame]);

  const findCharacter = useCallback((id: string) => characters.find((character) => character.id === id), []);

  const changeFastMode = useCallback((active: boolean) => {
    setFastMode(active);
    setTurboNotice(active);
    if (active) eggs.unlock('turbo-attract');
  }, [eggs.unlock]);

  const unlockFinaleShortcut = useCallback(() => {
    if (finaleShortcutVisible || finaleShortcutRoute) return;
    pause();
    setFinaleShortcutVisible(true);
  }, [finaleShortcutRoute, finaleShortcutVisible, pause]);

  const enterFinaleShortcut = useCallback(() => {
    const finaleIndex = entries.findIndex((candidate) => candidate.type === 'finale');
    const storyIndex = entries.findIndex((candidate) => candidate.type === 'story' && candidate.storyId === 'convergence-02');
    if (storyIndex < 0 || finaleIndex < 0) {
      setFinaleShortcutVisible(false);
      resume();
      return;
    }
    setFinaleShortcutVisible(false);
    setFinaleShortcutRoute({ finaleIndex });
    jumpToEntry(storyIndex, 16000);
  }, [entries, jumpToEntry, resume]);

  useEffect(() => {
    if (!finaleShortcutRoute) return;
    const timer = window.setTimeout(() => {
      jumpToEntry(finaleShortcutRoute.finaleIndex);
      setFinaleShortcutRoute(null);
      resume();
    }, 4000);
    return () => window.clearTimeout(timer);
  }, [finaleShortcutRoute, jumpToEntry, resume]);

  const armCharacter = useCallback((character: CharacterProfileType) => {
    const previous = eggs.armedFighterIds.filter((id) => id !== character.id);
    const slot = Math.min(2, previous.length + 1);
    eggs.armFighter(character.id);
    setArmedToast({ character, slot });
    attractAudio.playIfUnlocked(audioAsset(`/assets/audio/easter-eggs/${character.id}-unacceptable.wav`), { kind: 'voice', gain: 0.95 });
    window.setTimeout(() => setArmedToast((current) => current?.character.id === character.id ? null : current), 2500);
  }, [eggs]);

  useEffect(() => {
    if (introVisible || championShown || eggs.championRank || !eggs.allCoreEggsUnlocked || isPaused) return;
    setChampionShown(true);
    setChampionVisible(true);
    pause();
  }, [championShown, eggs.allCoreEggsUnlocked, eggs.championRank, introVisible, isPaused, pause]);

  useEffect(() => {
    let announcement = 'The Smackers attract mode';
    if (effectiveEntry.type === 'character') {
      const character = findCharacter(effectiveEntry.characterId);
      if (character) announcement = `Now featuring ${character.name}, ${character.nickname}`;
    } else if (effectiveEntry.type === 'story') announcement = 'Now showing an arcade story chapter';
    else if (effectiveEntry.type === 'versus') announcement = 'Now showing a versus match introduction';
    else if (effectiveEntry.type === 'character-select') announcement = 'Now showing a character select demonstration';
    else if (effectiveEntry.type === 'finale') announcement = 'Final boss revealed: The Convergence';
    else announcement = 'The Smackers title screen';
    document.title = `${announcement} — The Smackers`;
    const liveRegion = document.getElementById('profile-announcement');
    if (liveRegion) liveRegion.textContent = announcement;
  }, [effectiveEntry, findCharacter]);

  useEffect(() => {
    const next = entries[entryIndex + 1];
    if (!next) return;
    const urls: string[] = [];
    if (next.type === 'character') {
      const fighter = findCharacter(next.characterId);
      if (fighter) urls.push(fighter.images[0].src);
    } else if (next.type === 'story') {
      const story = getStory(next.storyId);
      if (story?.shots[0]) urls.push(story.shots[0].image);
    } else if (next.type === 'versus') {
      urls.push(`/assets/characters/${next.leftCharacterId}/idle/previews/${next.leftCharacterId}_idle_preview.gif?v=20260717-5`);
      urls.push(`/assets/characters/${next.rightCharacterId}/idle/previews/${next.rightCharacterId}_idle_preview.gif?v=20260717-5`);
    } else if (next.type === 'character-select') {
      urls.push(...characters.slice(0, 4).map((fighter) => `/assets/characters/${fighter.id}/idle/previews/${fighter.id}_idle_preview.gif?v=20260717-5`));
    } else if (next.type === 'finale') urls.push('/assets/attract/finale/the-convergence-4k.webp');
    const preloads = urls.map((url) => { const image = new Image(); image.src = url; return image; });
    return () => preloads.forEach((image) => { image.src = ''; });
  }, [entries, entryIndex, findCharacter]);

  const finishIntro = useCallback(() => {
    sessionStorage.setItem(introKey, 'true');
    setIntroVisible(false);
    begin();
  }, [begin]);

  const renderScene = () => {
    const playFightingGame = () => { pause(); setShooterMode(false); setFightMode('1p'); };
    const playShooter = () => { pause(); setFightMode(null); setShooterMode(true); setShooterRunKey((current) => current + 1); };
    const retryShooter = () => { pause(); setShooterMode(true); setShooterRunKey((current) => current + 1); };
    if (fightMode) return <FightingGame characters={characters} mode={fightMode} onPlayShooter={playShooter} onExit={() => { setFightMode(null); resume(); }} />;
    if (shooterMode) return <FinaleAttractScene key={`shooter-${shooterRunKey}`} onRestart={() => { setShooterMode(false); restart(); }} onRetryShooter={retryShooter} onPlayFightingGame={playFightingGame} characters={characters} />;
    if (effectiveEntry.type === 'character') {
      const character = findCharacter(effectiveEntry.characterId);
      if (!character) return <main className="fatal-error"><h1>FIGHTER SIGNAL LOST</h1></main>;
      const nextCharacterEntry = entries.slice(entryIndex + 1).find((candidate) => candidate.type === 'character');
      const nextCharacter = nextCharacterEntry?.type === 'character' ? findCharacter(nextCharacterEntry.characterId) ?? character : character;
      return <CharacterProfile key={character.id} character={character} nextCharacter={nextCharacter} imageIndex={imageIndex} frameProgress={frameProgress} onArm={armCharacter} />;
    }
    if (effectiveEntry.type === 'title') return <TitleAttractScene />;
    if (effectiveEntry.type === 'story') return <StoryAttractScene storyId={effectiveEntry.storyId} elapsedMs={sceneElapsedMs} />;
    if (effectiveEntry.type === 'versus') return <VersusAttractScene left={findCharacter(effectiveEntry.leftCharacterId)} right={findCharacter(effectiveEntry.rightCharacterId)} stage={effectiveEntry.stageId} elapsedMs={sceneElapsedMs} fastMode={fastMode} cameo={effectiveEntry.easterEgg ? findCharacter(effectiveEntry.easterEgg.cameoCharacterId) : undefined} matchupKey={effectiveEntry.easterEgg?.matchupKey} onPause={pause} onResume={resume} onDiscover={eggs.unlock} paused={isPaused} />;
    if (effectiveEntry.type === 'character-select') return <CharacterSelectAttractScene characters={characters} elapsedMs={sceneElapsedMs} fastMode={fastMode} />;
    return <FinaleAttractScene key={`campaign-shooter-${shooterRunKey}`} onRestart={restart} onRetryShooter={retryShooter} onPlayFightingGame={playFightingGame} characters={characters} />;
  };

  return (
    <div className={`app-shell${isPaused && !fightMode && !shooterMode ? ' is-attract-paused' : ''}${fastMode ? ' is-fast-mode' : ''}`}>
      <p id="profile-announcement" className="sr-only" aria-live="polite" aria-atomic="true" />
      <ArcadeMarquee credits={credits} fastMode={fastMode} onFastChange={changeFastMode} onFinaleShortcut={unlockFinaleShortcut} />
      {renderScene()}
      {!introVisible && !fightMode && !shooterMode && <AttractPressStart enabled={credits >= 2} onStart={() => startFightingGame('1p')} paused={isPaused} playbackRate={playbackRate} />}
      {!reducedMotion && entryIndex > 0 && <CharacterTransition style={transitionStyles[entryIndex % transitionStyles.length]} sequence={entryIndex} />}
      <SiteFooter />
      {armedToast && <FighterArmToast character={armedToast.character} slot={armedToast.slot} />}
      {turboNotice && <aside className="turbo-toast" aria-live="assertive"><strong>TURBO ATTRACT</strong><span>HOLD ACTIVE // CABINET CLOCK 4X</span></aside>}
      {coinNotice && <aside key={coinNotice.sequence} className="coin-credit-toast" aria-live="assertive"><strong>GULP!</strong><span>CREDIT ADDED // {coinNotice.credits}/2</span></aside>}
      {championVisible && <ChampionUnlock existingName={eggs.playerName} unlocks={eggs.unlocked} onComplete={eggs.rememberChampion} onReturn={() => { setChampionVisible(false); resume(); }} />}
      {finaleShortcutVisible && <FinaleShortcutUnlock onComplete={enterFinaleShortcut} />}
      {introVisible && <IntroSequence onComplete={finishIntro} reducedMotion={reducedMotion} />}
    </div>
  );
}
